<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useStatsStore } from '@/stores/stats'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { storeToRefs } from 'pinia'

const props = defineProps<{
  bubbleUuid: string
  didLabel?: string
  placeholder?: string
}>()

const statsStore = useStatsStore()
const { stats } = storeToRefs(statsStore)

const review = ref('')
const rating = ref(0)
const previewRating = ref(0)
const isEditing = ref(false)

interface ReviewObject {
  review: string
  createdAt: string
  updatedAt: string
}

const getReviewText = (reviewData: string | ReviewObject | null | undefined): string => {
  if (!reviewData) return ''
  if (typeof reviewData === 'object') return reviewData.review
  return reviewData
}

// Update local state when stats change
watch(
  () => stats.value[props.bubbleUuid],
  (newStats) => {
    if (newStats) {
      // Only update review if we're not currently editing and there's a new review value
      if (!isEditing.value) {
        review.value = getReviewText(newStats.review)
      }
      rating.value = newStats.rating || 0
      previewRating.value = newStats.rating || 0
    }
  },
  { immediate: true }
)

onMounted(async () => {
  await statsStore.fetchBubbleStats(props.bubbleUuid)
})

const toggleDid = async () => {
  await statsStore.toggleDid(props.bubbleUuid)
}

const updateRating = async (newRating: number) => {
  rating.value = newRating
  previewRating.value = newRating
  await statsStore.updateRating(props.bubbleUuid, newRating)
}

const handleMouseEnter = (value: number) => {
  previewRating.value = value
}

const handleMouseLeave = () => {
  previewRating.value = rating.value
}

const saveReview = async () => {
  if (!review.value.trim()) {
    return
  }
  await statsStore.updateReview(props.bubbleUuid, review.value)
  isEditing.value = false
}

const startEditing = () => {
  const currentStats = stats.value[props.bubbleUuid]
  review.value = currentStats?.review || ''
  isEditing.value = true
}

const cancelEditing = () => {
  const currentStats = stats.value[props.bubbleUuid]
  review.value = currentStats?.review || ''
  isEditing.value = false
}
</script>

<template>
  <div
    class="space-y-6 p-6 bg-card rounded-lg border border-border shadow-sm transition-all duration-200 hover:shadow-md dark:bg-card/50 dark:border-border/50"
  >
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <Button
        variant="outline"
        size="lg"
        :class="{
          'bg-primary text-primary-foreground hover:bg-primary/90 transition-colors duration-200':
            stats[bubbleUuid]?.did,
          'hover:bg-muted/80': !stats[bubbleUuid]?.did,
        }"
        @click="toggleDid"
      >
        <Icon
          :name="(stats[bubbleUuid]?.did ?? false) ? 'ph:check-circle-fill' : 'ph:circle'"
          class="w-5 h-5 mr-2 transition-transform duration-200"
          :class="{ 'scale-110': stats[bubbleUuid]?.did }"
        />
        {{ didLabel || 'Did it' }}
      </Button>

      <div class="flex gap-1 items-center">
        <button
          v-for="i in 5"
          :key="i"
          class="relative group"
          @click="updateRating(i)"
          @mouseenter="handleMouseEnter(i)"
          @mouseleave="handleMouseLeave"
        >
          <Icon
            name="ph:star-fill"
            class="w-7 h-7 transition-all duration-200"
            :class="[
              i <= (previewRating || 0)
                ? 'text-yellow-400 dark:text-yellow-500 scale-110'
                : 'text-muted dark:text-muted/50 scale-100',
            ]"
          />
          <span class="sr-only">Rate {{ i }} stars</span>
        </button>
      </div>
    </div>

    <div class="space-y-3">
      <div
        v-if="!isEditing && stats[bubbleUuid]?.review"
        class="relative group rounded-lg p-4 bg-muted/50 dark:bg-muted/10"
      >
        <p class="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
          {{ getReviewText(stats[bubbleUuid]?.review) }}
        </p>
        <Button
          variant="ghost"
          size="sm"
          class="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-all duration-200"
          @click="startEditing"
        >
          <Icon name="ph:pencil" class="w-4 h-4 mr-1" />
          Edit
        </Button>
      </div>

      <div
        v-if="isEditing || !stats[bubbleUuid]?.review"
        class="space-y-3 animate-in fade-in slide-in-from-top-2 duration-200"
      >
        <Textarea
          v-model="review"
          :placeholder="placeholder || 'Write your thoughts...'"
          class="min-h-[120px] resize-none bg-background dark:bg-card"
        />
        <div class="flex justify-end gap-2">
          <Button variant="outline" size="sm" class="hover:bg-muted/80" @click="cancelEditing">
            Cancel
          </Button>
          <Button
            size="sm"
            class="bg-primary hover:bg-primary/90 text-primary-foreground"
            @click="saveReview"
          >
            <Icon name="ph:check" class="w-4 h-4 mr-1" />
            Save Review
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.animate-in {
  animation: animate-in 0.2s ease-out;
}

@keyframes animate-in {
  from {
    opacity: 0;
    transform: translateY(-0.5rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
